import { cn } from 'utils/styles';
import { ImageWidth } from 'types/constants';
import { CardStyleIndicator } from 'components/shared/Card/CardStyle';
import { BaseCard, BaseCardBox, BaseCardContent } from 'components/shared/Card/BaseCard';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import HyperlinkedMedia from 'components/shared/HyperlinkedMedia/HyperlinkedMedia';
import Media from 'components/shared/Media/Media';
import { CardsItemImageLayout } from 'types/constants';
import { CardImageItem } from '../BaseCard/types';

interface CardImageItemProps extends Omit<CardImageItem, '__typename' | 'name'> {
  index: number;
  total: number;
  cardStyle: string;
}

function CardsImageItem({
  sys,
  index,
  total,
  cardImage,
  cardHeadlineRte,
  cardCopy,
  color,
  cardStyle,
  imageWidth,
  hyperlinkedImagesCollection,
  imageLayout,
  testId,
}: CardImageItemProps) {
  const imageSpacingStyles: Record<ImageWidth, string> = {
    [ImageWidth.Contained]: 'px-6 pb-6 sm:px-12 sm:pb-12',
    [ImageWidth.FullWidth]: 'flex-auto p-0 rounded-b-2xl',
  };

  const imageStyles: Record<ImageWidth, string> = {
    [ImageWidth.Contained]: 'object-contain',
    [ImageWidth.FullWidth]: 'object-cover rounded-b-2xl',
  };

  const imageWrapperClasses = cn(
    imageSpacingStyles[imageWidth],
    'max-h-[280px] sm:h-[400px] sm:max-h-[400px]', // Image wrapper needs a static height so images are aligned across the carousel despite varied slide content lengths
  );

  const testFixture = testId ?? 'cards-image-item';

  return (
    <BaseCard color={color.name} data-testid={`${testFixture}-base`}>
      <BaseCardBox data-testid={`${testFixture}-box`}>
        <CardStyleIndicator
          data-testid={testFixture}
          cardStyle={cardStyle}
          color={color.name}
          className="mb-6"
          index={index}
          total={total}
        />
        <BaseCardContent data-testid={testFixture} sys={sys} cardHeadlineRte={cardHeadlineRte} cardCopy={cardCopy} />
      </BaseCardBox>
      <div
        data-testid={`${testFixture}-media-wrapper`}
        className={imageWrapperClasses}
        {...liveUpdateAttrs(sys?.id, 'cardImage')}>
        {cardImage != null ? (
          <Media
            media={cardImage}
            sizes="(max-width: 425px) 100vw, 425px"
            priority
            className={cn('w-full h-full', imageStyles[imageWidth])}
            data-testid={`${testFixture}-image`}
          />
        ) : null}
        {hyperlinkedImagesCollection?.items != null && hyperlinkedImagesCollection.items.length > 0 ? (
          <div
            className="flex flex-row flex-wrap gap-2"
            {...liveUpdateAttrs(sys?.id, 'hyperlinkedImages')}
            data-testid={`${testFixture}-image-collection-list`}>
            {hyperlinkedImagesCollection.items.map((media, index) => {
              return (
                <HyperlinkedMedia
                  link={media.link}
                  image={media.image}
                  key={Buffer.from(media.image.url).toString('hex')}
                  targetHeight={imageLayout === CardsItemImageLayout.Horizontal ? 40 : undefined}
                  data-testid={`${testFixture}-image-collection-list-item-${index}`}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </BaseCard>
  );
}

export default CardsImageItem;
